import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from 'core/utils/localStorage';
import jwtDecode from 'jwt-decode';
import { isEmpty, isNil } from 'lodash';
import { autorun, computed, makeAutoObservable, runInAction, toJS } from 'mobx';
import authService from 'services/authService';
import userService from 'services/userService';
import type RootStore from './rootStore';
import type { AccessTokenData } from 'models/accessTokenData';
import type AccountData from 'models/accountData';
import type { SignInRequest } from 'services/dtoModels/auth/signInQuery';
import type { UpdateUserRequest } from 'services/dtoModels/user/updateUserRequest';

export default class AuthStore {
  accessToken: string | null = null;
  refreshToken: string | null = null;

  readonly isAuthenticated = computed(
    () => !isEmpty(this.accessToken) && !isEmpty(this.refreshToken)
  );

  constructor(public rootStore: RootStore) {
    makeAutoObservable(this);

    this.accessToken = getAccessToken();
    this.refreshToken = getRefreshToken();

    autorun(() => {
      const value = toJS(this);

      setAccessToken(value.accessToken);
      setRefreshToken(value.refreshToken);
    });
  }

  async signIn(data: SignInRequest): Promise<void> {
    const { accessToken, refreshToken } = await authService.signIn(data);

    runInAction(() => {
      this.accessToken = accessToken;
      this.refreshToken = refreshToken;
    });

    await this.setUserData(accessToken);
  }

  async signOut(): Promise<void> {
    await authService.signOut();

    runInAction(() => {
      this.accessToken = null;
      this.refreshToken = null;
    });

    await this.setUserData(null);
  }

  async updateToken(): Promise<void> {
    const { accessToken, refreshToken } = await authService.updateToken({
      refreshToken: this.refreshToken ?? '',
    });
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    await this.setUserData(this.accessToken);
  }

  async updateCurrentUser(
    userData: Omit<UpdateUserRequest, 'role' | 'actionAccesses' | 'blocked' | 'id'>
  ): Promise<void> {
    const currentUserId = this.rootStore.userStore.userData?.id;
    if (isNil(currentUserId) || currentUserId === 0) {
      throw new Error('Пользователь не авторизован');
    }

    try {
      const currentUser = this.rootStore.userStore.userData;
      if (!currentUser) {
        throw new Error('Пользователь не авторизован');
      }

      const updatedUser = await userService.updateUserAsync(currentUser.id, {
        ...userData,
        // Сохраняем текущие значения критичных полей безопасности
        role: currentUser.role,
        actionAccesses: currentUser.actionAccess,
        blocked: false,
      });

      // Обновляем данные в store, но сохраняем критичные поля из текущей сессии
      const user: AccountData = {
        ...updatedUser,
        shopId: currentUser.shopId,
        actionAccess: currentUser.actionAccess,
        permissions: currentUser.permissions,
      };

      this.rootStore.userStore.setUserData(user);
    } catch (error) {
      console.error('Ошибка при обновлении данных пользователя:', error);
      this.rootStore.notifyStore.showNotify(
        'Не удалось обновить данные пользователя. Попробуйте позже.',
        'error'
      );
      throw error;
    }
  }

  private async setUserData(accessToken: string | null): Promise<void> {
    let user: AccountData | null = null;

    if (!isNil(accessToken) && accessToken.length > 0) {
      try {
        const tokenData = jwtDecode<AccessTokenData>(accessToken);
        if (!tokenData?.Id || !tokenData?.ShopId) {
          throw new Error('Invalid token data: missing required fields');
        }

        const userId = Number(tokenData.Id);
        const shopId = Number(tokenData.ShopId);
        if (isNaN(userId) || isNaN(shopId)) {
          throw new Error('Invalid token data: ID or ShopId is not a number');
        }

        const userData = await userService.getUserAsync(userId);

        if (isNil(userData?.id) || isNil(userData?.role)) {
          throw new Error('Invalid user data: missing required fields');
        }

        user = {
          id: userData.id,
          shopId,
          name: userData.name || '',
          surname: userData.surname || '',
          patronymic: userData.patronymic || '',
          phone: userData.phone || '',
          role: userData.role,
          actionAccess: Array.isArray(userData.actionAccesses)
            ? userData.actionAccesses
            : [],
          permissions: [],
        };
      } catch (error) {
        console.error('Authentication error:', error);

        // В случае любой ошибки при обработке токена или получении данных
        // очищаем токены для безопасности
        runInAction(() => {
          this.accessToken = null;
          this.refreshToken = null;
        });

        user = null;

        this.rootStore.notifyStore.showNotify(
          'Ошибка аутентификации. Пожалуйста, войдите снова.',
          'error'
        );
      }
    }

    // Устанавливаем данные пользователя (null в случае ошибки или отсутствия токена)
    this.rootStore.userStore.setUserData(user);
  }
}
